/* Unpublished functional cart feedback. No analytics, storage, or network requests. */
(() => {
  'use strict';
  if (window.IgneousCartFeedback) return;
  let observed = null;
  let revision = 0;
  let lastCrossing = null;
  let lastQuantityCrossing = null;
  let navigating = false;
  let navigationRevision = 0;
  const consumed = new WeakSet();
  const nativeProofs = new WeakSet();
  const nativeReads = new WeakMap();
  const nativeWrites = new WeakMap();
  const pendingWrites = new Set();
  let nativeGeneration = 0, nativeSeed = null, nativeObserved = null;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const bursts = new Set();
  function snapshot(cart) {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || !/^[A-Z]{3}$/.test(cart.currency)) return null;
    let count = 0;
    const lines = [];
    for (const item of cart.items) {
      const variant = String(item.variant_id || item.id);
      const product = String(item.product_id);
      if (!/^[1-9]\d{0,15}$/.test(variant) || !/^[1-9]\d{0,15}$/.test(product) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return null;
      count += item.quantity;
      lines.push([String(item.key || variant), product, variant, item.quantity].join(':'));
    }
    if (count !== cart.item_count) return null;
    const fingerprint = lines.sort().join('|');
    const basket = JSON.stringify(cart.items.map(item => JSON.stringify([String(item.product_id), String(item.variant_id || item.id), item.quantity, item.properties, item.selling_plan_allocation?.selling_plan?.id])).sort());
    return {cart, fingerprint, basket, state: JSON.stringify([cart.currency, cart.total_price, fingerprint])};
  }
  function candidates() {
    return [...document.querySelectorAll('.igneous-cart-experiment [data-cart-message], .igneous-cart-drawer [data-cart-message]')];
  }
  function qualifies(cart, message) {
    const rate = Number(window.Shopify?.currency?.rate);
    const threshold = Number(message.getAttribute('data-limit')) * 100 * rate;
    return message.getAttribute('data-cart-message') === 'true' && Number.isFinite(threshold) && threshold > 0 && Number.isFinite(rate) && rate > 0 && cart.total_price >= threshold && cart.total_price !== 0;
  }
  function visibleCircle(message) {
    const circle = message.querySelector('.cart__graph');
    if (!circle || !message.classList.contains('is-success')) return null;
    if (circle.closest?.('[inert], [aria-hidden="true"], [hidden]')) return null;
    const box = circle.getBoundingClientRect();
    const style = getComputedStyle(circle);
    if (!box.width || !box.height || style.visibility === 'hidden' || style.display === 'none' || box.right <= 0 || box.bottom <= 0 || box.left >= innerWidth || box.top >= innerHeight) return null;
    return {circle, box};
  }
  function clearBursts() { bursts.forEach(burst => burst.remove()); bursts.clear(); }
  reduced.addEventListener?.('change', event => { if (event.matches) clearBursts(); });
  function poof(box) {
    if (reduced.matches || document.visibilityState === 'hidden') return 0;
    const burst = document.createElement('span');
    burst.className = 'igneous-shipping-poof';
    burst.setAttribute('aria-hidden', 'true');
    burst.style.left = `${box.left + box.width / 2}px`;
    burst.style.top = `${box.top + box.height / 2}px`;
    for (let i = 0; i < 10; i++) {
      const particle = document.createElement('i');
      const angle = i * Math.PI * 2 / 10;
      const distance = 19 + (i % 3) * 5;
      particle.style.setProperty('--x', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--y', `${Math.sin(angle) * distance - 5}px`);
      particle.style.setProperty('--r', `${i * 47}deg`);
      burst.appendChild(particle);
    }
    bursts.add(burst);
    document.body.appendChild(burst);
    setTimeout(() => { burst.remove(); bursts.delete(burst); }, 480);
    return 480;
  }
  function confirm(proof) {
    if (!proof || typeof proof !== 'object' || consumed.has(proof)) return 0;
    consumed.add(proof);
    const before = snapshot(proof.before);
    const current = snapshot(proof.current);
    if (!before || !current || before.cart.currency !== current.cart.currency || before.state === current.state || proof.rate !== Number(window.Shopify?.currency?.rate)) return 0;
    if (!['quantity', 'quick-add', 'discount', 'native-primary'].includes(proof.source)) return 0;
    if (proof.source === 'native-primary' && !nativeProofs.delete(proof)) return 0;
    if (proof.source !== 'discount' && before.fingerprint === current.fingerprint) return 0;
    let quantityCrossing = null;
    if (proof.source === 'quantity') {
      if (typeof proof.key !== 'string' || !Number.isSafeInteger(proof.quantity) || proof.quantity < 0) return 0;
      const target = before.cart.items.find(item => item.key === proof.key);
      if (!target || target.quantity === proof.quantity) return 0;
      const expectedItems = before.cart.items.flatMap(item => item.key === proof.key ? (proof.quantity ? [{...item, quantity: proof.quantity}] : []) : [item]);
      const expected = snapshot({...before.cart, items: expectedItems, item_count: before.cart.item_count - target.quantity + proof.quantity});
      const plainBasket = cart => {
        const variants = new Set(), keys = new Set(), rows = [];
        for (const item of cart.items) {
          for (const value of [item.product_id, item.variant_id, item.id]) {
            if (value !== undefined && typeof value !== 'string' && typeof value !== 'number') return null;
            if (typeof value === 'number' && (!Number.isSafeInteger(value) || value <= 0)) return null;
          }
          const product = String(item.product_id), variant = String(item.variant_id || item.id);
          if (variants.has(variant) || typeof item.key !== 'string' || !item.key || item.key.length > 256 || keys.has(item.key)) return null;
          if (item.id !== undefined && item.variant_id !== undefined && String(item.id) !== String(item.variant_id)) return null;
          if (item.properties != null && ((Object.prototype.toString.call(item.properties) !== '[object Object]' && !Array.isArray(item.properties)) || Object.keys(item.properties).length)) return null;
          if (item.selling_plan_allocation != null || item.selling_plan != null || item.parent_relationship != null || item.parent_id != null || item.parent_line_key != null || item.has_components != null && item.has_components !== false || item.remote != null && item.remote !== false) return null;
          if (item.item_components != null && (!Array.isArray(item.item_components) || item.item_components.length)) return null;
          variants.add(variant); keys.add(item.key); rows.push([product, variant, item.quantity]);
        }
        return JSON.stringify(rows.sort((a, b) => a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0));
      };
      const beforeBasket = plainBasket(before.cart), expectedBasket = expected && plainBasket(expected.cart), currentBasket = plainBasket(current.cart);
      const plainMatch = beforeBasket !== null && expectedBasket !== null && currentBasket !== null && expectedBasket === currentBasket;
      if (!expected || expected.fingerprint !== current.fingerprint && !plainMatch) return 0;
      if (plainMatch) quantityCrossing = JSON.stringify(['quantity', before.cart.currency, before.cart.total_price, beforeBasket, current.cart.currency, current.cart.total_price, currentBasket]);
    }
    if (proof.source === 'quick-add') {
      if (before.cart.items.some(item => String(item.product_id) === String(proof.product))) return 0;
      const added = current.cart.items.filter(item => String(item.product_id) === String(proof.product) && String(item.variant_id || item.id) === String(proof.variant));
      if (added.length !== 1 || added[0].quantity !== 1) return 0;
      const remaining = current.cart.items.filter(item => item !== added[0]);
      const expected = snapshot({...current.cart, items: remaining, item_count: current.cart.item_count - 1});
      if (!expected || expected.fingerprint !== before.fingerprint) return 0;
    }
    if (proof.source === 'discount') {
      const codes = cart => Array.isArray(cart.discount_codes) && cart.discount_codes.every(x => typeof x?.code === 'string' && typeof x.applicable === 'boolean') ? JSON.stringify(cart.discount_codes.map(x => [x.code.toUpperCase(), x.applicable]).sort()) : null;
      const previousCodes = codes(before.cart), currentCodes = codes(current.cart);
      if (previousCodes === null || currentCodes === null || previousCodes === currentCodes || before.basket !== current.basket) return 0;
    }
    const messages = candidates();
    // Only another confirmed below-threshold edit rearms a repeated crossing.
    if (messages.some(message => qualifies(before.cart, message) && !qualifies(current.cart, message))) { lastCrossing = null; lastQuantityCrossing = null; }
    const crossing = messages.filter(message => !qualifies(before.cart, message) && qualifies(current.cart, message));
    if (!crossing.length) return 0;
    const key = `${before.state}\n${current.state}`;
    if (key === lastCrossing || quantityCrossing && quantityCrossing === lastQuantityCrossing) return 0;
    lastCrossing = key;
    if (quantityCrossing) lastQuantityCrossing = quantityCrossing;
    // Consume the crossing even if hidden/reduced: it must never replay on open/reload.
    const visible = crossing.map(visibleCircle).find(Boolean);
    return visible ? poof(visible.box) : 0;
  }
  function nativeSnapshot(cart) {
    try {
      if (!cart || !Array.isArray(cart.items) || cart.items.length > 1000 || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || !/^[A-Z]{3}$/.test(cart.currency)) return null;
      if (cart.total_discount !== 0 || cart.original_total_price !== undefined && cart.original_total_price !== cart.total_price) return null;
      if (!Array.isArray(cart.cart_level_discount_applications) || cart.cart_level_discount_applications.length) return null;
      if (cart.line_count !== undefined && cart.line_count !== cart.items.length) return null;
      if (cart.token !== undefined && (typeof cart.token !== 'string' || !cart.token || cart.token.length > 2048)) return null;
      for (const name of ['discount_codes', 'cart_level_discount_applications']) if (cart[name] !== undefined && (!Array.isArray(cart[name]) || cart[name].length)) return null;
      const keys = new Set(), variants = new Set(), items = [];
      let count = 0;
      for (const item of cart.items) {
        const line = nativeLine(item);
        if (!line || keys.has(line.key) || variants.has(line.variant)) return null;
        keys.add(line.key); variants.add(line.variant); items.push(line); count += line.quantity;
        if (!Number.isSafeInteger(count)) return null;
      }
      if (count !== cart.item_count) return null;
      const token = cart.token === undefined ? null : cart.token;
      const state = JSON.stringify([cart.currency, cart.total_price, token, items.map(item => [item.key, item.product, item.variant, item.quantity]).sort((a, b) => a[0].localeCompare(b[0]))]);
      return {items, count, token, state, cart: {currency: cart.currency, total_price: cart.total_price, total_discount: 0, item_count: count, items: items.map(item => ({key: item.key, product_id: item.product, variant_id: item.variant, quantity: item.quantity}))}};
    } catch (_) { return null; }
  }
  function nativeLine(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    for (const value of [item.product_id, item.variant_id, item.id]) if (typeof value === 'number' && (!Number.isSafeInteger(value) || value <= 0)) return null;
    const product = String(item.product_id), variant = String(item.variant_id || item.id);
    if (!/^[1-9]\d{0,15}$/.test(product) || !/^[1-9]\d{0,15}$/.test(variant) || typeof item.key !== 'string' || !item.key || item.key.length > 256 || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return null;
    if (item.id !== undefined && item.variant_id !== undefined && String(item.id) !== String(item.variant_id)) return null;
    if (item.properties != null && (Object.prototype.toString.call(item.properties) !== '[object Object]' && !Array.isArray(item.properties) || Object.keys(item.properties).length)) return null;
    if (item.selling_plan_allocation != null || item.selling_plan != null || item.parent_relationship != null || item.parent_id != null || item.parent_line_key != null || item.has_components === true || item.remote === true) return null;
    if (item.total_discount !== undefined && item.total_discount !== 0) return null;
    for (const name of ['item_components', 'discount_allocations', 'line_level_discount_allocations', 'discounts']) if (item[name] !== undefined && (!Array.isArray(item[name]) || item[name].length)) return null;
    return {key: item.key, product, variant, quantity: item.quantity};
  }
  function readStart() {
    const token = {};
    nativeReads.set(token, {generation: nativeGeneration, revision, idle: pendingWrites.size === 0});
    return token;
  }
  function acceptRead(token, cart) {
    const read = token && nativeReads.get(token);
    if (!read) return false;
    nativeReads.delete(token);
    if (!read.idle || read.generation !== nativeGeneration || read.revision !== revision || pendingWrites.size) return false;
    nativeSeed = nativeSnapshot(cart);
    return Boolean(nativeSeed);
  }
  function beginWrite() {
    const lease = {};
    const isolated = pendingWrites.size === 0;
    for (const pending of pendingWrites) pending.valid = false;
    const record = {generation: ++nativeGeneration, valid: isolated, before: nativeSeed, rate: Number(window.Shopify?.currency?.rate), publications: [], started: Date.now()};
    nativeSeed = null;
    nativeWrites.set(lease, record); pendingWrites.add(record);
    return lease;
  }
  function finishWrite(lease, cart) {
    const record = lease && nativeWrites.get(lease);
    if (!record) return null;
    nativeWrites.delete(lease);
    const isolated = record.valid && record.generation === nativeGeneration && pendingWrites.size === 1;
    pendingWrites.delete(record);
    const current = nativeSnapshot(cart);
    const agreed = isolated && current && nativeObserved?.state === current.state && record.publications.length > 0 && record.publications.every(state => state === current.state);
    if (agreed) nativeSeed = current;
    return {record, current, agreed};
  }
  function endWrite(lease, cart) { return Boolean(finishWrite(lease, cart)?.agreed); }
  function beginPrimary(form, data) {
    const lease = beginWrite(), record = nativeWrites.get(lease);
    record.form = form;
    try {
      if (!form?.isConnected || !form.closest('[data-section-type="product"]') || form.closest('[data-quickview-modal]') || !(data instanceof FormData)) return lease;
      const ids = data.getAll('id'), quantities = data.getAll('quantity');
      if (ids.length !== 1 || quantities.length > 1 || [...data.keys()].some(name => name === 'selling_plan' || name.startsWith('properties['))) return lease;
      const variant = String(ids[0]), amount = quantities.length ? String(quantities[0]) : '1', quantity = Number(amount);
      if (!/^[1-9]\d{0,15}$/.test(variant) || !/^[1-9]\d*$/.test(amount) || !Number.isSafeInteger(quantity) || quantity > 100) return lease;
      record.wanted = {variant, quantity};
    } catch (_) {}
    return lease;
  }
  function cancelPrimary(lease) {
    const record = lease && nativeWrites.get(lease);
    if (!record) return;
    record.valid = false; nativeWrites.delete(lease); pendingWrites.delete(record); nativeSeed = null; nativeGeneration++;
  }
  function confirmPrimary(lease, acknowledgement, cart) {
    const finished = finishWrite(lease, cart);
    if (!finished?.agreed) return 0;
    const {record, current} = finished, before = record.before, wanted = record.wanted;
    if (!before || !wanted || !record.form?.isConnected || Date.now() - record.started > 20000 || record.rate !== Number(window.Shopify?.currency?.rate) || before.cart.currency !== current.cart.currency || before.token !== current.token) return 0;
    const ack = nativeLine(acknowledgement);
    if (!ack || ack.variant !== wanted.variant || current.count - before.count !== wanted.quantity) return 0;
    const old = before.items.find(item => item.variant === wanted.variant), added = current.items.find(item => item.variant === wanted.variant);
    if (!added || added.product !== ack.product || added.key !== ack.key || added.quantity !== ack.quantity || old && old.product !== ack.product || added.quantity - (old?.quantity || 0) !== wanted.quantity) return 0;
    const others = value => JSON.stringify(value.items.filter(item => item.variant !== wanted.variant).map(item => [item.key, item.product, item.variant, item.quantity]).sort((a, b) => a[0].localeCompare(b[0])));
    if (others(before) !== others(current)) return 0;
    const proof = {before: before.cart, current: current.cart, source: 'native-primary', rate: record.rate};
    nativeProofs.add(proof);
    return confirm(proof);
  }
  function resetNative() {
    nativeGeneration++; nativeSeed = null; nativeObserved = null;
    for (const pending of pendingWrites) pending.valid = false;
    pendingWrites.clear();
  }
  const safely = (method, fallback) => (...args) => { try { return method(...args); } catch (_) { return fallback; } };
  window.IgneousCartFeedback = {
    capture(fingerprint) { return observed?.fingerprint === fingerprint ? {cart: observed.cart, revision, rate: Number(window.Shopify?.currency?.rate)} : null; },
    isCurrent(capture) { return Boolean(capture && capture.revision === revision); },
    confirm(proof) { try { return confirm(proof); } catch (_) { return 0; } },
    readStart: safely(readStart, null), acceptRead: safely(acceptRead, false),
    beginWrite: safely(beginWrite, null), endWrite: safely(endWrite, false),
    beginPrimary: safely(beginPrimary, null), cancelPrimary: safely(cancelPrimary, null), confirmPrimary: safely(confirmPrimary, 0)
  };
  document.addEventListener('theme:cart:change', event => {
    revision++;
    nativeObserved = nativeSnapshot(event.detail?.cart);
    if (nativeSeed && nativeSeed.state !== nativeObserved?.state) nativeSeed = null;
    for (const pending of pendingWrites) {
      if (pending.publications.length >= 16) pending.valid = false;
      else pending.publications.push(nativeObserved?.state || null);
    }
    try { observed = snapshot(event.detail?.cart); } catch (_) { observed = null; }
  });
  document.addEventListener('igneous:cart:confirmed', event => window.IgneousCartFeedback.confirm(event.detail));
  function busy(link) {
    const drawer = link.closest('.igneous-cart-drawer');
    const scope = drawer?.closest('[data-section-type="cart-drawer"]');
    return Boolean(scope?.getAttribute('aria-busy') === 'true' || drawer?.querySelector('[data-cart-loading].cart--loading') || drawer?.querySelector('form.cart[aria-busy="true"]'));
  }
  function syncNavigation() {
    document.querySelectorAll('.igneous-cart-drawer [data-cart-review-checkout]').forEach(link => {
      if (busy(link) || navigating) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('.igneous-cart-drawer [data-cart-review-checkout]');
    if (!link) return;
    if (busy(link) || navigating) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;
    const revision = ++navigationRevision;
    navigating = true;
    syncNavigation();
    // A later listener may cancel this click. Keep the immediate duplicate fence,
    // then release only this canceled attempt after native event dispatch ends.
    setTimeout(() => {
      if (revision === navigationRevision && event.defaultPrevented) {
        navigating = false;
        syncNavigation();
      }
    }, 0);
  }, true);
  // The lab drawer always reviews the cart; accidental form submission cannot bypass it.
  document.addEventListener('submit', event => {
    if (event.target.matches('.igneous-cart-drawer form.cart') && event.target.querySelector('[data-cart-review-checkout]')) event.preventDefault();
  }, true);
  const drawer = document.querySelector('.igneous-cart-drawer');
  if (drawer) new MutationObserver(syncNavigation).observe(drawer, {subtree: true, attributes: true, attributeFilter: ['class', 'aria-busy'], childList: true});
  window.addEventListener('pageshow', () => { navigationRevision++; navigating = false; clearBursts(); syncNavigation(); });
  window.addEventListener('pageshow', event => { if (event.persisted) resetNative(); }, true);
  window.addEventListener('pagehide', () => { clearBursts(); resetNative(); });
  syncNavigation();
})();
